import { BAR_WIDTH, Indicator } from '@phaser/minigames/fishing/Indicator';
import { UtilityGear } from '@phaser/minigames/_common/UtilityGear';
import { ItemDropObject } from '@phaser/objects/ItemDrop.object';
import { weightedRandom } from '@phaser/helpers/Math';
import { Fish } from '@phaser/minigames/fishing/Fish';
import { DEPTH } from '@phaser/data/depth';
import { Game } from '@phaser/scenes/Game';
import * as Shared from '@shared/lib';

export const FISH_TIMEOUT = 8_000;
const CELL_SIZE = 70;
const X_PAD = BAR_WIDTH / 2;
const Y_PAD = 128;

export class FishingMinigame {
  public get difficulty() {
    return this._utilityGear?.getDifficultyMultiplier() ?? 1;
  }

  private _scene: Game = Game.current;

  private _indicator?: Indicator;
  private _utilityGear?: UtilityGear;
  private _fish: Map<string, Fish> = new Map();
  private _closeButton?: Phaser.GameObjects.Image;
  private _container?: Phaser.GameObjects.Container;
  private _particles?: Phaser.GameObjects.Particles.ParticleEmitter;

  private _spawnTimeout?: Timer;
  private _currentFish?: string;
  private _gridCols: number;
  private _gridRows: number;

  constructor() {}

  public start() {
    if (!this._utilityGear) {
      this._utilityGear = new UtilityGear('fishing');
    }

    this._placeBackground();
    this._calculateGrid();
    this._spawn();
  }

  public restart() {
    this.destroy();
    this.start();
  }

  public update(time: number, delta: number) {
    this._indicator?.update(time, delta);
  }

  public destroy() {
    clearTimeout(this._spawnTimeout);

    this._fish.forEach(f => f.destroy());
    this._fish.clear();

    this._closeButton?.destroy();
    this._container?.destroy();
    this._indicator?.destroy();
    this._particles?.destroy();
  }

  public collectFish() {
    const fish = this._fish.get(this._currentFish!);
    if (!fish) return;

    fish.destroy();
    this._indicator?.destroy();
    this._currentFish = undefined;
    this._indicator = undefined;
    this._fish.delete(fish.id);

    const drop = new Shared.LootDropMessage({
      amount: 1,
      id: fish.keyId,
      type: Shared.LootDropType.Item,
    });
    const itemDrop = new ItemDropObject(fish.x, fish.y, drop);
    itemDrop.setDepth(DEPTH.MINIGAME + 1);

    this._scene.multiplayerSystem.gotFish(fish.keyId, 1);
  }

  public stopFishing(id: string) {
    if (this._currentFish === id) {
      this._indicator?.destroy();
      this._currentFish = undefined;
      this._indicator = undefined;
    }
    this._fish.get(id)?.destroy();
    this._fish.delete(id);
  }

  public onPointerDown(fishId: string) {
    const fish = this._fish.get(fishId);
    if (!fish) return this.destroy();

    if (this._currentFish === fishId && this._indicator) {
      this._indicator?.onPointerDown();
    } else {
      if (this._indicator) this._indicator.destroy();
      this._indicator = new Indicator(this, fish);

      this._currentFish = fishId;
    }
  }

  public onPointerUp(fishId: string) {
    if (this._currentFish !== fishId || !this._indicator) return;
    this._indicator.onPointerUp();
  }

  private _placeBackground() {
    const camera = this._scene.cameras.main;
    const width = camera.displayWidth;
    const height = camera.displayHeight;

    const imageScale = Math.max(width / 2048, height / 2048);

    this._container = this._scene.add.container(width / 2, height / 2)
      .setDepth(DEPTH.MINIGAME);

    const background = this._scene.add.image(0, 0, 'water')
      .setScale(imageScale);
    this._container.add(background);

    this._particles = this._scene.add.particles(0, 0, 'sprites', {
      frame: { frames: ['glow'] },
      lifespan: { min: 1000, max: 8000 },
      maxAliveParticles: 40,
      scale: { min: 0.005, max: 0.01 },
      speed: [-15, -10, 10, 15],
      alpha: { values: [0, 1, 0], interpolation: 'catmull', ease: 'linear' },
      tint: 0x007B6A,
      quantity: 0.1,
      emitZone: { type: 'random', source: new Phaser.Geom.Rectangle(0, 0, width, height), quantity: 1 },
    }).setDepth(DEPTH.MINIGAME);

    this._closeButton = this._scene.add.image(width - 30, 30, 'sprites', 'close')
      .setDepth(DEPTH.MINIGAME)
      .setScale(0.1)
      .setInteractive();

    this._closeButton.on('pointerdown', () => {
      this._scene.multiplayerSystem.leaveGame();
    });
  }

  private _calculateGrid() {
    const camera = this._scene.cameras.main;
    const width = camera.displayWidth;
    const height = camera.displayHeight;

    this._gridCols = Math.floor((width - X_PAD * 2) / CELL_SIZE);
    this._gridRows = Math.floor((height - Y_PAD * 2) / CELL_SIZE);
  }

  private _spawn(): void {
    const x = Math.floor(Math.random() * this._gridCols) * CELL_SIZE;
    const y = Math.floor(Math.random() * this._gridRows) * CELL_SIZE;

    const baseX = this._container!.x - (CELL_SIZE * this._gridCols) / 2 + CELL_SIZE / 2;
    const baseY = this._container!.y - (CELL_SIZE * this._gridRows) / 2 + CELL_SIZE / 2;

    const id = `${x}-${y}`;
    const xPos = baseX + x;
    const yPos = baseY + y;

    if (!this._currentFish && !this._fish.has(id)) {
      const activeBaits = this._utilityGear?.getActiveItems() ?? [];
      const hasLevel1 = activeBaits.some(i => i === 'fishingBait1');
      const hasLevel2 = activeBaits.some(i => i === 'fishingBait2');

      const filteredFish = Shared.fishData
        .filter(f => {
          if (!hasLevel1 && !hasLevel2) {
            return f.strength <= 4;
          }
          else if (hasLevel1 && !hasLevel2) {
            return f.strength >= 5 && f.strength <= 7;
          }
          else if (hasLevel2) {
            return f.strength >= 7;
          }
        });
      const fish = weightedRandom(filteredFish.map(o => o.spawnChance), filteredFish);
      if (!fish) return this._spawn();

      this._fish.set(id, new Fish(this, id, fish, xPos, yPos));
    }

    this._spawnTimeout = setTimeout(() => {
      this._spawn();
    }, Math.random() * 4_000 + 4_000);
  }
}